import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { H1Title } from "../../components/typography/typography";
import api from "../../services/api";
import NotificationService from "../../services/notificationService";
import { Error as ToastError } from "../../components/toast/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers, faGavel, faFileAlt, faCheckCircle, faTimesCircle, faClock, faEdit, faHistory } from "@fortawesome/free-solid-svg-icons";

function timeAgo(date) {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);

  useEffect(() => { loadStats(); loadActivity(); }, []);

  const loadStats = async () => {
    try {
      const { data } = await api.get('/admin/dashboard');
      setStats(data.data.stats);
    } catch (err) {
      ToastError('Failed to load dashboard');
    } finally { setLoading(false); }
  };

  const loadActivity = async () => {
    try {
      const { data } = await NotificationService.getMyNotifications({ limit: 15 });
      setActivity(data.data.notifications || []);
    } catch { /* */ }
    finally { setActivityLoading(false); }
  };

  if (loading) return <div className="py-20 text-center text-grey-667">Loading...</div>;
  if (!stats) return (
    <div className="text-center py-20">
      <H1Title size="sm">Admin Dashboard</H1Title>
      <p className="text-grey-667 mt-2">No active competition. <Link to="/admin/competition" className="text-primary hover:underline">Create one</Link></p>
    </div>
  );

  const s = stats.submissions;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <H1Title size="sm">Admin Dashboard</H1Title>
        <div className="text-sm text-grey-667">
          {stats.competition.name} {stats.competition.year} — Round {stats.competition.currentRound} ({stats.competition.status})
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-grey-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-primary-bg rounded-lg flex items-center justify-center">
              <FontAwesomeIcon icon={faUsers} className="text-primary" />
            </div>
            <p className="text-2xl font-bold">{stats.participants}</p>
          </div>
          <p className="text-xs text-grey-667">Registered Participants</p>
        </div>
        <div className="bg-white rounded-xl border border-grey-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center">
              <FontAwesomeIcon icon={faGavel} className="text-orange-500" />
            </div>
            <p className="text-2xl font-bold">{stats.judges}</p>
          </div>
          <p className="text-xs text-grey-667">Judges</p>
        </div>
        <div className="bg-white rounded-xl border border-grey-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-green-light rounded-lg flex items-center justify-center">
              <FontAwesomeIcon icon={faFileAlt} className="text-green" />
            </div>
            <p className="text-2xl font-bold">{s.total}</p>
          </div>
          <p className="text-xs text-grey-667">Total Submissions</p>
        </div>
        <div className="bg-white rounded-xl border border-grey-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-green-light rounded-lg flex items-center justify-center">
              <FontAwesomeIcon icon={faCheckCircle} className="text-green" />
            </div>
            <p className="text-2xl font-bold">{s.advanced}</p>
          </div>
          <p className="text-xs text-grey-667">Advanced</p>
        </div>
      </div>

      {/* Submission Breakdown */}
      <div className="bg-white rounded-xl border border-grey-200 p-5">
        <h3 className="font-bold mb-4">Submission Breakdown</h3>
        <div className="grid grid-cols-5 gap-3">
          {[
            { label: 'Draft', count: s.draft, icon: faEdit, color: 'text-grey-667', bg: 'bg-grey-200' },
            { label: 'Submitted', count: s.submitted, icon: faFileAlt, color: 'text-primary', bg: 'bg-primary-bg' },
            { label: 'Advanced', count: s.advanced, icon: faCheckCircle, color: 'text-green', bg: 'bg-green-light' },
            { label: 'Eliminated', count: s.eliminated, icon: faTimesCircle, color: 'text-red-500', bg: 'bg-red-500/10' },
            { label: 'Total', count: s.total, icon: faClock, color: 'text-grey-dark', bg: 'bg-grey-bg' },
          ].map((item, i) => (
            <div key={i} className={`rounded-lg p-3 text-center ${item.bg}`}>
              <FontAwesomeIcon icon={item.icon} className={`${item.color} mb-1`} />
              <p className="text-xl font-bold">{item.count}</p>
              <p className="text-xs text-grey-667">{item.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl border border-grey-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold">
            <FontAwesomeIcon icon={faHistory} className="mr-2 text-primary" /> Recent Activity
          </h3>
          <Link to="/notifications" className="text-xs text-primary hover:underline">View all</Link>
        </div>
        {activityLoading ? (
          <p className="text-sm text-grey-667 text-center py-6">Loading...</p>
        ) : activity.length === 0 ? (
          <p className="text-sm text-grey-667 text-center py-6">No recent activity. Notifications from participant actions (milestone updates, monthly reports) will appear here.</p>
        ) : (
          <div className="divide-y divide-grey-200 max-h-[360px] overflow-y-auto client-overflow-scroll">
            {activity.map((n) => (
              <div key={n._id} className={`py-3 flex items-start gap-3 ${n.isRead ? '' : 'bg-primary-bg/20 -mx-2 px-2 rounded'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${n.isRead ? 'bg-grey-200' : 'bg-primary-bg'}`}>
                  <FontAwesomeIcon icon={n.isRead ? faCheckCircle : faClock} className={`text-xs ${n.isRead ? 'text-grey-667' : 'text-primary'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className={`text-sm truncate ${n.isRead ? 'font-medium text-grey-dark' : 'font-semibold text-black'}`}>{n.title}</p>
                    <span className="text-xs text-grey-98 flex-shrink-0">{timeAgo(n.createdAt)}</span>
                  </div>
                  <p className="text-xs text-grey-667 truncate mt-0.5">{n.body}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Links */}
      <div className="grid md:grid-cols-3 gap-4">
        {[
          { to: '/admin/submissions', label: 'View All Submissions', desc: 'Review and manage proposals' },
          { to: '/admin/judges', label: 'Judge Management', desc: 'Assign judges and view scores' },
          { to: '/admin/competition', label: 'Competition Settings', desc: 'Manage rounds and location brief' },
        ].map((link, i) => (
          <Link key={i} to={link.to} className="bg-white rounded-xl border border-grey-200 p-5 hover:border-primary hover:shadow-sm transition-all">
            <p className="font-bold text-sm">{link.label}</p>
            <p className="text-xs text-grey-667 mt-1">{link.desc}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
